import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { authService } from '@/services/auth.service'
import { toast } from '@/hooks/use-toast'
import AnimatedLogo from '@/components/AnimatedLogo'
import { getErrorMessage } from '@/utils/error'

type Status = 'verifying' | 'success' | 'error'

/**
 * Ativa a conta a partir do link do e-mail. Confirma via POST no mount —
 * guardado por ref pra rodar UMA vez (StrictMode + link-scanners que só fazem
 * GET não disparam o POST). Sucesso → redireciona pro login; erro → permite
 * reenviar o e-mail de ativação.
 */
export default function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [status, setStatus] = useState<Status>('verifying')
  const [resendEmail, setResendEmail] = useState('')
  const [isResending, setIsResending] = useState(false)
  const ranRef = useRef(false)

  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true

    if (!token) {
      setStatus('error')
      return
    }

    authService
      .verifyEmail(token)
      .then(() => {
        setStatus('success')
        setTimeout(() => navigate('/auth'), 3000)
      })
      .catch(() => setStatus('error'))
  }, [token, navigate])

  async function handleResend(e: React.FormEvent) {
    e.preventDefault()
    if (!resendEmail.trim()) return
    setIsResending(true)
    try {
      const res = await authService.resendVerification(resendEmail)
      toast({ title: 'E-mail reenviado!', description: res.message })
    } catch (error: unknown) {
      toast({
        title: 'Erro ao reenviar',
        description: getErrorMessage(error, 'Tente novamente em instantes.'),
        variant: 'destructive',
      })
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
      <Card className="w-full max-w-md glass-card">
        {status === 'verifying' && (
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AnimatedLogo size="lg" />
            </div>
            <CardTitle className="text-2xl">Ativando sua conta...</CardTitle>
            <CardDescription>Aguarde enquanto confirmamos seu e-mail.</CardDescription>
            <div className="flex justify-center pt-4">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          </CardHeader>
        )}

        {status === 'success' && (
          <>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AnimatedLogo size="lg" />
              </div>
              <div className="flex justify-center mb-4">
                <CheckCircle2 className="w-16 h-16 text-green-500" />
              </div>
              <CardTitle className="text-2xl">Conta ativada!</CardTitle>
              <CardDescription>
                Tudo certo — sua conta foi ativada com sucesso. Redirecionando para o login...
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate('/auth')}>
                Ir para o login
              </Button>
            </CardContent>
          </>
        )}

        {status === 'error' && (
          <>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AnimatedLogo size="lg" />
              </div>
              <div className="flex justify-center mb-4">
                <XCircle className="w-16 h-16 text-destructive" />
              </div>
              <CardTitle className="text-2xl">Link inválido ou expirado</CardTitle>
              <CardDescription>
                Este link de ativação não é mais válido. Informe seu e-mail para receber um novo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleResend} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="resend-email">E-mail</Label>
                  <Input
                    id="resend-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isResending}>
                  {isResending ? 'Reenviando...' : 'Reenviar link de ativação'}
                </Button>
              </form>

              <div className="pt-4 border-t">
                <Link
                  to="/auth"
                  className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar para o login
                </Link>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  )
}
